<template>
  <div class="mt-4 text-caption">
    <div
      v-if="feature.properties.cell_id"
      v-html="createRow('ID', feature.properties.cell_id, 'string')"
    ></div>
    <div
      class="mt-2"
      v-if="feature.properties.tile_count"
      v-html="tileCountWithPlatformCounts"
    ></div>
    <div
      class="mt-2"
      v-if="feature.properties.min_date"
      v-html="createRow('Minimum Date', feature.properties.min_date, 'date')"
    ></div>
    <div
      v-if="feature.properties.max_date"
      v-html="createRow('Maximum Date', feature.properties.max_date, 'date')"
    ></div>
    <div
      v-if="feature.properties.modified"
      v-html="createRow('Modified (UTC)', feature.properties.modified, 'utc')"
    ></div>
    <div class="mt-2">
      <v-data-table
        :headers="tableHeaders"
        :items="tableData"
        hide-default-footer
        dense
        id="dataTable"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex text-caption">
            <div class="text--secondary">{{ item.name }}</div>
            <tooltip-modal class_def="px-2">
              {{ item.description }}
            </tooltip-modal>
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import TooltipModal from "@/components/shared/TooltipModal";

export default {
  props: {
    feature: {
      type: Object,
      required: true,
    },
  },
  components: {
    TooltipModal,
  },
  computed: {
    tileCountWithPlatformCounts() {
      return `${this.createRow(
        "Tile Count",
        this.feature.properties.tile_count,
        "number"
      )}${
        this.feature.properties.platforms
          ? ` <span style="font-size: smaller;"><span class="text--secondary">(</span>${this.getPlatforms()}<span class="text--secondary">)</span></span>`
          : ""
      }`;
    },
    platformTableHeaders() {
      return [
        {
          text: "",
          align: "start",
          sortable: false,
          value: "name",
        },
        {
          text: "Geoeye-01",
          value: "geoeye-01",
          sortable: false,
          align: "center",
          class: "text--secondary",
        },
        {
          text: "Worldview-01",
          value: "worldview-01",
          sortable: false,
          align: "center",
          class: "text--secondary",
        },
        {
          text: "Worldview-02",
          value: "worldview-02",
          sortable: false,
          align: "center",
          class: "text--secondary",
        },
        {
          text: "Worldview-03",
          value: "worldview-03",
          sortable: false,
          align: "center",
          class: "text--secondary",
        },
        {
          text: "Worldview-04",
          value: "worldview-04",
          sortable: false,
          align: "center",
          class: "text--secondary",
        },
      ];
    },
    tableHeaders() {
      return [
        {
          text: "",
          align: "start",
          sortable: false,
          value: "name",
        },
        {
          text: "90%",
          value: "ninety",
          sortable: false,
          align: "center",
          class: "text--secondary",
        },
        { text: "75%", value: "seventyFive", sortable: false, align: "center" },
        { text: "50%", value: "fifty", sortable: false, align: "center" },
        { text: "25%", value: "twentyFive", sortable: false, align: "center" },
      ];
    },
    platformsTableData() {
      return [
        {
          name: "Count",
          "geoeye-01": this.getPlatformCount("geoeye-01"),
          "worldview-01": this.getPlatformCount("worldview-01"),
          "worldview-02": this.getPlatformCount("worldview-02"),
          "worldview-03": this.getPlatformCount("worldview-03"),
          "worldview-04": this.getPlatformCount("worldview-04"),
        },
      ];
    },
    tableData() {
      return [
        {
          name: "Data",
          description: "Count of tiles with data found by coverage percentage",
          ninety: this.feature.properties.data_percentage_90
            ? Number(
                this.feature.properties.data_percentage_90
              ).toLocaleString()
            : "",
          seventyFive: this.feature.properties.data_percentage_75
            ? Number(
                this.feature.properties.data_percentage_75
              ).toLocaleString()
            : "",
          fifty: this.feature.properties.data_percentage_50
            ? Number(
                this.feature.properties.data_percentage_50
              ).toLocaleString()
            : "",
          twentyFive: this.feature.properties.data_percentage_25
            ? Number(
                this.feature.properties.data_percentage_25
              ).toLocaleString()
            : "",
        },
        {
          name: "Cloud Free",
          description: "Count of cloud free tiles found by percentage",
          ninety: this.feature.properties.cloud_free_percentage_90
            ? Number(
                this.feature.properties.cloud_free_percentage_90
              ).toLocaleString()
            : "",
          seventyFive: this.feature.properties.cloud_free_percentage_75
            ? Number(
                this.feature.properties.cloud_free_percentage_75
              ).toLocaleString()
            : "",
          fifty: this.feature.properties.cloud_free_percentage_50
            ? Number(
                this.feature.properties.cloud_free_percentage_50
              ).toLocaleString()
            : "",
          twentyFive: this.feature.properties.cloud_free_percentage_25
            ? Number(
                this.feature.properties.cloud_free_percentage_25
              ).toLocaleString()
            : "",
        },
      ];
    },
  },
  methods: {
    formatValue(type, value) {
      switch (type) {
        case "number":
          return Number(value).toLocaleString();
        case "percent":
          return `${value}%`;
        case "date":
          return new Date(value).toLocaleString();
        case "array":
          return value.join(",");
        case "utc":
          return new Date(value).toUTCString();
      }
      return value;
    },
    createRow(label, value, type = "string") {
      return `<span><span class="text--secondary">${label}:</span>&nbsp;${this.formatValue(
        type,
        value
      )}</span>`;
    },
    getPlatforms() {
      let platformsText = "";

      try {
        let index = 0;
        const platforms = JSON.parse(this.feature.properties.platforms);
        for (const platform in platforms) {
          if (platforms[platform]) {
            const upperCaseStr = (str) => {
              return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
            };

            platformsText += `<span class="text--secondary">${
              index > 0 ? `, ` : ""
            }${upperCaseStr(platform)}:</span> ${Number(
              platforms[platform]
            ).toLocaleString()}`;
            index++;
          }
        }
      } catch (err) {
        console.error(err);
      }

      return platformsText;
    },
    getPlatformCount(platform) {
      try {
        const platforms = JSON.parse(this.feature.properties.platforms);
        for (const p in platforms) {
          if (platforms[p] && p === platform) {
            return Number(platforms[p]).toLocaleString();
          }
        }
      } catch (err) {
        console.error(err);
      }

      return "";
    },
  },
};
</script>

<style>
/* remove left and right padding on first and last cells so it lines up with the rest of the metadata */
#dataTable.v-data-table
  > div.v-data-table__wrapper
  > table
  > thead.v-data-table-header
  > tr
  > th:first-child,
#dataTable.v-data-table
  > div.v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:first-child {
  padding-left: 0px;
}
#dataTable.v-data-table
  > div.v-data-table__wrapper
  > table
  > thead.v-data-table-header
  > tr
  > th:last-child,
#dataTable.v-data-table
  > div.v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:last-child {
  padding-right: 0px;
}
</style>
